import { useEffect } from 'react';
import { View } from '@shared/components/View';
import { useFocusTrap } from '@shared/util/hooks';
import { getController } from '@shared/util/timeout';
import { AnchoredViewProps } from './props';
import styles from './AnchoredView.module.css';

export function AnchoredView({
  children,
  anchorName,
  className,
  style,
  onClose,
  lightDismiss = true,
  focusFirstInput = true,
  trapFocus = true,
  ...rest
}: AnchoredViewProps) {
  const popoverRef = useFocusTrap(trapFocus);
  const popoverState = lightDismiss ? 'auto' : 'manual';

  useEffect(() => {
    const el = popoverRef.current;
    el?.showPopover();

    if (focusFirstInput) {
      requestAnimationFrame(() => {
        el?.querySelector('input')?.focus();
      });
    }

    const { signal, abort } = getController();
    const onToggle = (event: Event) => {
      const toggleEvent = event as ToggleEvent;
      if (toggleEvent.newState === 'closed') {
        onClose();
      }
    };
    el?.addEventListener('toggle', onToggle, { signal });
    return abort;
  }, [onClose, focusFirstInput, popoverRef]);

  return (
    <View
      ref={popoverRef}
      popover={popoverState}
      className={[styles.anchoredView, className]}
      style={{ positionAnchor: anchorName, ...style }}
      {...rest}
    >
      {children}
    </View>
  );
}
